import { Directive, ElementRef, HostListener, Output, EventEmitter, Input, SimpleChanges, OnChanges, inject } from '@angular/core';

@Directive({
  selector: '[appImagePan]',
  standalone: true
})
export class ImagePanDirective implements OnChanges {
  @Input() panState: { x: number, y: number, scale: number } | null = null;
  @Output() panEnd = new EventEmitter<{ x: number, y: number, scale: number }>();

  private el = inject(ElementRef<HTMLElement>);

  private isDragging = false;
  private startX = 0;
  private startY = 0;
  private translateX = 0;
  private translateY = 0;
  private scale = 1;
  private initialDistance = 0;
  private initialScale = 1;

  constructor() {
    this.el.nativeElement.style.cursor = 'grab';
    this.el.nativeElement.style.position = 'absolute';
    this.el.nativeElement.draggable = false;
    this.updateTransform();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['panState'] && changes['panState'].currentValue) {
      const state = changes['panState'].currentValue;
      this.translateX = state.x;
      this.translateY = state.y;
      this.scale = state.scale;
      this.updateTransform();
    }
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent) {
    event.preventDefault();
    this.startDrag(event.clientX, event.clientY);
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent) {
    if (event.touches.length === 1) {
      if (event.cancelable) event.preventDefault();
      this.startDrag(event.touches[0].clientX, event.touches[0].clientY);
    } else if (event.touches.length === 2) {
      if (event.cancelable) event.preventDefault();
      this.initialDistance = this.getDistance(event.touches);
      this.initialScale = this.scale;
    }
  }

  private getDistance(touches: TouchList) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private startDrag(x: number, y: number) {
    this.isDragging = true;
    this.startX = x - this.translateX;
    this.startY = y - this.translateY;
    this.el.nativeElement.style.cursor = 'grabbing';
  }

  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this.isDragging) return;
    event.preventDefault();
    this.drag(event.clientX, event.clientY);
  }

  @HostListener('window:touchmove', ['$event'])
  onTouchMove(event: TouchEvent) {
    if (event.touches.length === 1 && this.isDragging) {
      if (event.cancelable) event.preventDefault();
      this.drag(event.touches[0].clientX, event.touches[0].clientY);
    } else if (event.touches.length === 2) {
      if (event.cancelable) event.preventDefault();
      const currentDistance = this.getDistance(event.touches);
      const diff = currentDistance - this.initialDistance;
      this.scale = Math.max(0.1, Math.min(this.initialScale + diff * 0.01, 5));
      this.updateTransform();
    }
  }

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent) {
    event.preventDefault();
    const zoomSensitivity = 0.001;
    this.scale += event.deltaY * -zoomSensitivity;
    this.scale = Math.max(0.1, Math.min(this.scale, 5));
    this.updateTransform();
    this.emitChange();
  }

  private drag(x: number, y: number) {
    this.translateX = x - this.startX;
    this.translateY = y - this.startY;
    this.updateTransform();
  }

  @HostListener('window:mouseup')
  @HostListener('window:mouseleave')
  @HostListener('window:touchend')
  endDrag() {
    if (this.isDragging) {
      this.isDragging = false;
      this.el.nativeElement.style.cursor = 'grab';
      this.emitChange();
    } else if (event?.type === 'touchend') {
      this.emitChange();
    }
  }

  private emitChange() {
    this.panEnd.emit({ x: this.translateX, y: this.translateY, scale: this.scale });
  }

  private updateTransform() {
    this.el.nativeElement.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
  }
}
